/**
 * What the §F5.8 cascading filter's four selects are showing — derived from the one scope.
 *
 * Split out of `CascadeFilter.tsx` so it can be tested (and reasoned about) without a
 * renderer: it is the whole of the control's state model, and the component holds none of
 * its own. See that file for why the two hierarchies are branches rather than one path.
 */
import { COUNTRY_NAMES, type Country, type Scope } from '../../lib/state';

/** §F1's four programme countries plus "All countries", in the topbar's own order. */
export const CASCADE_COUNTRIES = Object.keys(COUNTRY_NAMES) as Country[];

export interface CascadeSelection {
  region: string;
  commune: string;
  watershed: string;
  partnership: string;
}

const NONE: CascadeSelection = { region: '', commune: '', watershed: '', partnership: '' };

/**
 * A scope names a single unit; the select *above* it shows that unit's parent, and the ones
 * below it are empty ("All …"). The other branch is empty throughout — which is the mutual
 * exclusion, and it needs no code because a region scope simply has no watershed in it.
 */
export function cascadeSelection(scope: Scope | null): CascadeSelection {
  if (!scope) return NONE;
  if (scope.unitType === 'admin') {
    if (scope.adminLevel === 1) return { ...NONE, region: scope.unitId };
    if (scope.adminLevel === 2) {
      return { ...NONE, region: scope.parentId ?? '', commune: scope.unitId };
    }
    return NONE; // an admin-0 scope is the country selector's job — see `Scope`
  }
  if (scope.unitType === 'watershed') return { ...NONE, watershed: scope.unitId };
  return { ...NONE, watershed: scope.parentId ?? '', partnership: scope.unitId };
}
